import styled from '@emotion/styled';
import { Box, Grid, IconButton, Link, Tooltip } from '@mui/material';
import { OrderJobTask } from '../../../redux/slices/slice_types';
import { useAppSelector } from '@/redux/hooks';
import { selectCanViewContentAssemblyTasks } from '@/redux/selectors/authSelectors';
import PolarisIcon from '../PolarisIcon';
import TaskMetadata from './TaskMetadata';
import TaskAccurateLink from './TaskAccurateLink';

export type TaskActionProps = {
  task: OrderJobTask;
  onRefresh?: () => void;
};

const TaskAction = ({ task, onRefresh }: TaskActionProps) => {
  const canViewContentAssemblyTasks = useAppSelector(selectCanViewContentAssemblyTasks);

  const getPackagingState = (task: OrderJobTask) => {
    if(task.status === 'complete') {
      return {
        tooltipText: 'Open in Packaging (Read-Only Mode)',
        isDisabled: false
      }
    }

    if (task.status !== 'ready' && task.status !== 'in_progress') {
      return {
        tooltipText: 'Task must be In Progress or Ready to open Packaging',
        isDisabled: true
      };
    }

    if (task.assigned_user === null) {
      return {
        tooltipText: 'User must be assigned to open Packaging',
        isDisabled: true
      };
    }

    return {
      tooltipText: 'Open in Packaging',
      isDisabled: false
    };
  };

  const renderAction = () => {
    switch (task.task_action?.type) {
      case 'link':
        return (
          <Link href={task.task_action?.action_url} target="_blank">
            <IconButton color="primary" sx={{ color: 'primary.main', width: 'fit-content' }} size="small">
              <PolarisIcon icon="external_link" height={24} />
            </IconButton>
          </Link>
        );
      case 'accurate':
        return <TaskAccurateLink task={task} />;
      case 'premiere':
        return (
          <Box sx={{ p: 0.25 }}>
            <PolarisIcon icon="premier_pro" color="#00005B" height={30} style={{ background: '#A5A3FF', padding: 1, borderRadius: 5 }} />
          </Box>
        );
      case 'json_schema':
        return <TaskMetadata task={task} onRefresh={onRefresh} />;
      case 'packaging': {
        const { tooltipText, isDisabled } = getPackagingState(task);

        return (
          <Tooltip title={tooltipText}>
            <div>
              <IconButton
                color="teal"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(`${window.origin}/packaging/${task.id}`, '_blank');
                }}
                disabled={isDisabled}
              >
                <PolarisIcon icon={'packaging'} height={30} />
              </IconButton>
            </div>
          </Tooltip>
        );
      }
      default:
        return (
          <span>
            <PolarisIcon icon="external_link" height={24} style={{ opacity: 0.2 }} />
          </span>
        );
    }
  };

  if (task.task_action?.type === 'packaging' && !canViewContentAssemblyTasks) {
    return null;
  }

  return (
    <ActionWrapper container alignItems="center">
      {renderAction()}
    </ActionWrapper>
  );
};

export default TaskAction;

const ActionWrapper = styled(Grid)`
  align-items: center;
  margin-left: ${({ theme }) => theme.spacing(0.75)};

  & .MuiButtonBase-root {
    font-size: 1.6rem;
    margin-top: ${({ theme }) => theme.spacing(-1)};
    margin-bottom: ${({ theme }) => theme.spacing(-1)};
    margin-left: ${({ theme }) => theme.spacing(-0.5)};
  }
`;
